import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useMemo } from "react";

import { ScoutInput } from "./ScoutInput";
import { SCOUT_SUGGESTIONS, SCOUT_SUGGESTIONS_DISPLAY_COUNT } from "./suggestions";

export function NewChat({
  onSubmit,
  onAbort,
  isSending,
  isAborting,
}: {
  onSubmit: (text: string) => Promise<void>;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
}) {
  const theme = useTheme();

  // Two rows of two chips. Explicit 2-row layout (rather than a single
  // flex-wrap row) keeps the surrounding inline-block hugging the chip
  // content's intrinsic width — a flex-wrap container is treated as
  // max-content by inline-block sizing and ends up stretching to nearly
  // the full viewport, which pushes the "Suggestions" caption away from
  // the leftmost chip's left edge.
  const suggestionRows = useMemo(() => {
    const picked = SCOUT_SUGGESTIONS.slice(0, SCOUT_SUGGESTIONS_DISPLAY_COUNT);
    const rows: (typeof picked)[] = [];
    for (let i = 0; i < picked.length; i += 2) {
      rows.push(picked.slice(i, i + 2));
    }
    return rows;
  }, []);

  return (
    <Stack
      sx={{
        flex: 1,
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
        paddingTop: { xs: 8, sm: 9 },
        paddingBottom: { xs: 10, sm: 12 },
        transform: "translateY(calc(-2vh - 100px))",
        gap: 5,
      }}
    >
      <ScoutIcon sx={{ color: theme.palette.encampBlue.main, width: 80, height: 80 }} />
      <Typography variant="h6" textAlign="center">
        Hey there! I'm Scout, your Encamp guide. How can I help you today?
      </Typography>

      {suggestionRows.length > 0 && (
        // Center via outer `text-align: center` on a 100%-width parent +
        // `display: inline-block` on the inner. inline-block is the only
        // reliable shrink-to-fit that doesn't get stretched by a flex-wrap
        // descendant. Each row is its own non-wrapping Stack so the inner
        // block's intrinsic width = widest row (no flex-wrap → no
        // max-content inflation). `justifyContent: center` on each row
        // balances the narrower row when the two rows have different
        // widths. Caption sits at the inner block's left edge, which
        // equals the leftmost chip on the widest row.
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Box sx={{ display: "inline-block", textAlign: "left", maxWidth: "100%" }}>
            <Typography variant="caption" color="text.secondary" pl={0.5}>
              Suggestions
            </Typography>
            <Stack spacing={1.5} mt={1}>
              {suggestionRows.map((row) => (
                <Stack
                  key={row.map((s) => s.label).join("|")}
                  direction="row"
                  spacing={1.5}
                  justifyContent="center"
                >
                  {row.map((suggestion) => (
                    <Button
                      key={suggestion.label}
                      variant="contained"
                      color="encampBlue"
                      disabled={isSending}
                      onClick={() => onSubmit(suggestion.prompt)}
                      sx={{ textTransform: "none" }}
                    >
                      {suggestion.label}
                    </Button>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      <ScoutInput
        onSubmit={onSubmit}
        onAbort={onAbort}
        isSending={isSending}
        isAborting={isAborting}
        placeholder="Check regulations, help me understand compliance requirements..."
      />
    </Stack>
  );
}
